/**
 * Шапка «только логотип» на экране со скрытой шапкой (слово владельца
 * 21.10.2026: Walk-fit, Dance-bit — на информационных экранах нет «назад»,
 * полосы и счётчика, но логотип воронки на своём месте). Поле экрана
 * `hideChrome` прячет кнопки, полосу, счётчик и крестик; логотип остаётся,
 * если он есть в обычной шапке этого экрана и у экрана нет `hideLogo`.
 * Клавиатура (пак `hideOnKeyboard`) прячет и логотип: она ставит оба признака
 * видимости (`resolveChromeVisibility`, quizChromeState.ts), значит
 * `hideLogo` — и логотипа нет.
 *
 * Здесь же стиль строки логотипа обычной шапки — одна дверь для обеих веток
 * (QuizScreenWizard.tsx зовёт `logoOwnRowStyle` и `LOGO_INLINE_CENTER_STYLE`),
 * чтобы логотип не разъехался между экранами со шапкой и без неё. Вынесено из
 * большого файла визарда.
 */
import React, { useEffect, useState } from 'react';
import type { ChromeVisibility } from './quizChromeState';
import type { QuizLogoAlign } from './quizHeader';
import { QuizLogoRow } from './QuizHeaderRow';

const LOGO_JUSTIFY = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
} as const;

/**
 * Строка логотипа над кнопками: выравнивание по полю, отступ до кнопок —
 * поле шапки `rowGap` (stylepack/header-insets), без поля прежние 8.
 */
export function logoOwnRowStyle(
  align: QuizLogoAlign,
  rowGap?: number,
): React.CSSProperties {
  return {
    display: 'flex',
    justifyContent: LOGO_JUSTIFY[align],
    marginBottom: rowGap ?? 8,
  };
}

/** Логотип по центру поверх строки кнопок (вне потока). */
export const LOGO_INLINE_CENTER_STYLE: React.CSSProperties = {
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  maxWidth: '50%',
  display: 'flex',
  pointerEvents: 'none',
};

/**
 * Ширина окна, с которой плеер — «компьютер»: та же граница, от которой
 * страница рисует лого «только на компьютере» (`.qb-desktop-logo` в
 * styles/public-paywall.css; совпадение держит тест-сосед).
 */
export const QUIZ_DESKTOP_MIN_WIDTH = 920;

/**
 * Окно плеера шире телефона (от QUIZ_DESKTOP_MIN_WIDTH), со слежением за
 * сменой ширины. Канва конструктора — всегда телефон: окно там мерит
 * редактор, а не посетителя (как у DesktopLogo.tsx).
 */
export function useDesktopViewport(canvas: boolean): boolean {
  const read = () =>
    !canvas &&
    typeof window !== 'undefined' &&
    window.innerWidth >= QUIZ_DESKTOP_MIN_WIDTH;
  const [desktop, setDesktop] = useState(read);
  useEffect(() => {
    const check = () => setDesktop(read());
    check();
    if (canvas) return undefined;
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
    // read зависит только от canvas
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvas]);
  return desktop;
}

/**
 * Оставить ли логотип на экране со скрытой шапкой. Шапка не скрыта — false
 * (решать нечего). «Узлы по разделам» показывают логотип только своим полем
 * положения (у пака эта шапка без логотипа) — так и здесь. На компьютере
 * (`desktop`) — false: решение владельца 23.10.2026 «рек» — логотип при
 * скрытой шапке остаётся только на телефоне, на компьютере шапки нет вовсе,
 * как было (и у старых воронок тоже).
 */
export function keepLogoWhenChromeHidden(input: {
  visibility: Pick<ChromeVisibility, 'hideChrome' | 'hideLogo'>;
  logo: { own: boolean } | null;
  sectionNodes: boolean;
  desktop?: boolean;
}): boolean {
  if (!input.visibility.hideChrome) return false;
  if (input.desktop) return false;
  if (input.visibility.hideLogo) return false;
  if (!input.logo) return false;
  return input.sectionNodes ? input.logo.own : true;
}

export type LogoOnlyPlace = 'logo-row' | 'inline-center' | 'row';

/** Где стоит логотип — там же, где в обычной шапке этого экрана. */
export function logoOnlyPlace(input: {
  logoInRow: boolean;
  logoInline: boolean;
  sectionNodes: boolean;
}): LogoOnlyPlace {
  if (input.logoInRow) return 'logo-row';
  if (input.logoInline && !input.sectionNodes) return 'inline-center';
  return 'row';
}

/**
 * Шапка из одного логотипа. Высота — как у строки логотипа обычной шапки:
 * в `logo-row` и «по центру поверх кнопок» место кнопки «назад» держит
 * невидимая распорка (`spacer`, как на первом экране), своей строкой —
 * только строка логотипа.
 */
export function QuizLogoOnlyChrome({
  logo,
  align,
  height,
  place,
  spacer,
  layoutStyle,
}: {
  logo: React.ReactNode;
  align: QuizLogoAlign;
  height: number;
  place: LogoOnlyPlace;
  spacer: React.ReactNode;
  layoutStyle?: React.CSSProperties;
}) {
  return (
    <div
      data-testid="quiz-screen-chrome"
      data-chrome="logo-only"
      style={{ marginBottom: 16, ...layoutStyle }}
    >
      {place === 'logo-row' ? (
        <QuizLogoRow
          back={spacer}
          logo={logo}
          logoAlign={align}
          counter={null}
        />
      ) : place === 'inline-center' ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            minHeight: height,
          }}
        >
          {spacer}
          <div data-logo-place="inline-center" style={LOGO_INLINE_CENTER_STYLE}>
            {logo}
          </div>
        </div>
      ) : (
        <div data-logo-place="row" style={logoOwnRowStyle(align)}>
          {logo}
        </div>
      )}
    </div>
  );
}
